import { ReactNode } from "react";
import { DateTime } from "luxon";
import { TableCell, TableRow } from "@mui/material";
import { UserInfo } from "app/containers/Global/types";
import { TeamCheckInUserItem } from "app/containers/TeamsTab/types";
import { SkeletonTableRow } from "../../layer01";
import { TeamTableRow } from "../../layer23";
import { StyledTable } from "../../layer24";

export type TeamTableProps = {
  items: TeamCheckInUserItem[];
  usersById: Record<number, UserInfo>;
  isLoading: boolean;
  onUserClick: (userAccountId: number, displayName: string) => void;
  onViewSummary: (userAccountId: number, date?: string | null) => void;
  hasDateRange?: boolean;
  firstHeaderNode?: ReactNode;
  emptyStateNode?: ReactNode;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (sortValue: string, direction: "asc" | "desc") => void;
  showNextOneOnOne?: boolean;
  nextOneOnOneByUserId?: Record<number, DateTime>;
};

export const TeamTable = ({
  items,
  usersById,
  isLoading,
  onUserClick,
  onViewSummary,
  hasDateRange = false,
  firstHeaderNode,
  emptyStateNode,
  sortBy,
  sortDirection,
  onSort,
  showNextOneOnOne = false,
  nextOneOnOneByUserId,
}: TeamTableProps) => {
  const columnCount = showNextOneOnOne ? 7 : 6;

  const getRows = () => {
    if (isLoading) {
      return <SkeletonTableRow rows={5} cellsPerRow={columnCount} />;
    }
    if (items.length === 0 && emptyStateNode) {
      return (
        <TableRow>
          <TableCell
            colSpan={columnCount}
            className="bg-teamApp-table-bg"
            sx={{
              padding: "3rem 1rem",
              border: 0,
              textAlign: "center",
            }}
          >
            {emptyStateNode}
          </TableCell>
        </TableRow>
      );
    }
    return items.map((item) => (
      <TeamTableRow
        key={item.userAccountId}
        item={item}
        userInfo={usersById[item.userAccountId]}
        onClick={onUserClick}
        onViewSummary={onViewSummary}
        hasDateRange={hasDateRange}
        showNextOneOnOne={showNextOneOnOne}
        nextOneOnOneDate={nextOneOnOneByUserId?.[item.userAccountId] ?? null}
      />
    ));
  };

  return (
    <StyledTable
      mobileEdgeToEdge
      headers={[
        firstHeaderNode ?? "Team Member",
        "Trending",
        hasDateRange ? "Last Completed" : "Completed Date",
        ...(showNextOneOnOne ? ["Next 1:1"] : []),
        "Latest Summary",
        hasDateRange ? "Check-ins" : "Check-in",
        "Actions",
      ]}
      chevronColumns={[]}
      sortableColumns={{
        "Team Member": "name",
        [hasDateRange ? "Check-ins" : "Check-in"]: "check-in-completed",
        Trending: "trending",
        [hasDateRange ? "Last Completed" : "Completed Date"]: "completionDate",
        "Latest Summary": "summary",
      }}
      columnWidths={{
        "Team Member": "16rem",
        "Next 1:1": "10rem",
      }}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onSort}
      rows={getRows()}
    />
  );
};
